import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Clock, MapPin, Calendar, Building2, Globe, Briefcase, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { usePageMeta } from '@/hooks/usePageMeta';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [pitchNote, setPitchNote] = useState('');
  const [hasApplied, setHasApplied] = useState(false);

  // Format date consistently as "15 Feb 2026"
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Set page meta based on project data
  usePageMeta({
    title: project ? `${project.title} | Talent Collective` : 'Project Details | Talent Collective',
    description: project ? project.description?.substring(0, 160) : 'View project details and apply.'
  });

  useEffect(() => {
    fetchProject();
    if (user?.role === 'talent') {
      checkExistingApplication();
    }
  }, [id, user]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Project not found.');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingApplication = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      const existingApp = response.data.find(app => app.project_id === id);
      if (existingApp) {
        setHasApplied(true);
      }
    } catch (error) {
      console.error('Error checking applications:', error);
    }
  };

  const handleApply = async () => {
    if (!user) {
      // Redirect to signup for non-logged-in users, preserving the return path
      navigate('/signup', { state: { from: `/projects/${id}` } });
      return;
    }

    if (user.role !== 'talent') {
      toast.error('Only professionals can apply to projects.');
      return;
    }

    if (!profile) {
      toast.error('Please complete your profile first.');
      navigate('/onboarding');
      return;
    }

    setShowApplyModal(true);
  };

  const submitApplication = async () => {
    if (pitchNote.length < 50) {
      toast.error('Please write at least 50 characters in your pitch.');
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/applications`, {
        project_id: id,
        pitch_note: pitchNote
      });
      toast.success('Thank you for applying! Our team will review your application and get back to you.');
      setShowApplyModal(false);
      setHasApplied(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application.');
    } finally {
      setApplying(false);
    }
  };

  const functionColors = {
    product: 'bg-teal-100 text-teal-700',
    design: 'bg-violet-100 text-violet-700',
    engineering: 'bg-blue-100 text-blue-700',
  };

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-custom py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/4 mb-8"></div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="h-96 bg-slate-200 rounded"></div>
              </div>
              <div className="h-64 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-detail-page">
      <Header />

      <div className="container-custom py-12">
        <Link to="/projects" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-link">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Opportunities
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-8" data-testid="project-header">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`${functionColors[project.function]} px-3 py-1 rounded-full text-xs font-semibold uppercase`}>
                      {capitalize(project.function)}
                    </span>
                    {project.is_featured && (
                      <span className="badge-accent">Featured</span>
                    )}
                  </div>
                  <h1 className="heading-2 mb-2">{project.title}</h1>
                  <p className="text-lg text-slate-600 flex items-center">
                    <Building2 className="h-5 w-5 mr-2" />
                    {project.company_name || 'Organization'}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center text-slate-600">
                  <Clock className="h-5 w-5 mr-2 text-slate-400" />
                  <span>{project.hours_per_week} hours/week</span>
                </div>
                <div className="flex items-center text-slate-600">
                  <Calendar className="h-5 w-5 mr-2 text-slate-400" />
                  <span>{project.duration}</span>
                </div>
                <div className="flex items-center text-slate-600">
                  <MapPin className="h-5 w-5 mr-2 text-slate-400" />
                  <span className="capitalize">{capitalize(project.location_type)}</span>
                </div>
                {project.timezone && (
                  <div className="flex items-center text-slate-600">
                    <Globe className="h-5 w-5 mr-2 text-slate-400" />
                    <span>{project.timezone}</span>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-8" data-testid="project-description">
              <h2 className="heading-3 mb-4">About the Project</h2>
              <p className="text-slate-600 whitespace-pre-wrap">{project.description}</p>
            </Card>

            <Card className="p-8" data-testid="project-responsibilities">
              <h2 className="heading-3 mb-4">Responsibilities</h2>
              {project.responsibilities ? (
                <ul className="text-slate-600 space-y-2 list-disc list-inside">
                  {project.responsibilities.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <li key={idx}>{line.replace(/^[-•*]\s*/, '').trim()}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 italic">No responsibilities listed.</p>
              )}
            </Card>

            <Card className="p-8" data-testid="project-ideal-candidate">
              <h2 className="heading-3 mb-4">Ideal Candidate</h2>
              {project.ideal_candidate ? (
                <ul className="text-slate-600 space-y-2 list-disc list-inside">
                  {project.ideal_candidate.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <li key={idx}>{line.replace(/^[-•*]\s*/, '').trim()}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 italic">No requirements listed.</p>
              )}
            </Card>

            {project.tools_required && project.tools_required.trim() && (
              <Card className="p-8" data-testid="project-tools">
                <h2 className="heading-3 mb-4">Tools & Skills Required</h2>
                <div className="flex flex-wrap gap-2">
                  {project.tools_required.split(',').map((tool, idx) => (
                    <span key={idx} className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full text-sm font-medium">
                      {tool.trim()}
                    </span>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card className="p-6 sticky top-24" data-testid="apply-card">
              {hasApplied ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">Application Submitted</h3>
                  <p className="text-sm text-slate-500 mb-4">Thank you! We'll review your application and get back to you.</p>
                  <Link to="/applications">
                    <Button variant="outline" className="w-full" data-testid="view-applications">
                      View My Applications
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <h3 className="font-semibold text-slate-900 mb-4">Interested in this project?</h3>
                  <Button 
                    className="btn-accent w-full mb-3" 
                    onClick={handleApply}
                    data-testid="apply-button"
                  >
                    Apply Now
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    {!user ? 'Create an account to apply.' : 
                     user.role !== 'talent' ? 'Only professionals can apply.' :
                     !profile ? 'Complete your profile to apply.' :
                     'Free to apply.'}
                  </p>
                </>
              )}
            </Card>

            {project.company_description && (
              <Card className="p-6" data-testid="company-info">
                <h3 className="font-semibold text-slate-900 mb-4">About the Organization</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center text-slate-600 mb-2">
                    <Building2 className="h-4 w-4 mr-3 text-slate-400" />
                    <span className="font-medium">{project.company_name || 'Organization'}</span>
                  </div>
                  <p className="text-slate-600">{project.company_description}</p>
                </div>
              </Card>
            )}

            <Card className="p-6" data-testid="quick-info">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration</span>
                  <span className="text-slate-900 font-medium">{project.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Hours/Week</span>
                  <span className="text-slate-900 font-medium">{project.hours_per_week}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Location</span>
                  <span className="text-slate-900 font-medium capitalize">{capitalize(project.location_type)}</span>
                </div>
                {project.start_date && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Start Date</span>
                    <span className="text-slate-900 font-medium">{formatDate(project.start_date)}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="sm:max-w-lg" data-testid="apply-modal">
          <DialogHeader>
            <DialogTitle>Apply to {project.title}</DialogTitle>
            <DialogDescription>
              Tell the organization why you're a great fit for this project.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Share why you're interested in this project and what makes you a great fit. Include relevant experience and how you can contribute."
              value={pitchNote}
              onChange={(e) => setPitchNote(e.target.value)}
              className="min-h-[200px]"
              data-testid="pitch-note-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              {pitchNote.length}/50 characters minimum
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyModal(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={submitApplication}
              disabled={applying || pitchNote.length < 50}
              data-testid="submit-application"
            >
              {applying ? 'Submitting...' : 'Submit Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
